// Pure, DOM-free rendering of captured line-item properties (APO v2 /
// theme `properties[...]` selections — see option-capture.ts) as small
// muted lines under a drawer item card. Kept in its own module (like
// option-capture.ts, product-button.ts) so it's unit-testable in the
// node-only vitest environment — index.ts itself can't be imported
// there (it calls boot() at module load, which needs a real `document`).
//
// Values render VERBATIM (e.g. "Liquid Propane (+$85.00)") — that's
// exactly the string the customer's own configurator showed them at
// selection time; never stripped or re-derived here.

/** Duplicated from index.ts's own `escapeHtml` (not imported — this
 *  module must stay import-free of index.ts to remain independently
 *  testable). Same five-character escape table. */
function escapeHtml(s: string): string {
  return (s || "").replace(/[&<>"']/g, (c) => (
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;"
  ));
}

/** Render captured properties as an HTML fragment for a card's options
 *  block — one escaped `<div class="qb-item-option">name: value</div>`
 *  per property, capped at `max` (default 5) with a trailing "+N more
 *  options" line when there's more (the full list stays on the item —
 *  this is display-only). No properties → `""`, so a caller can
 *  unconditionally append the result and an item with none renders
 *  byte-identical to before this existed. */
export function renderItemOptionsHtml(
  properties: Array<{ name: string; value: string }> | null | undefined,
  max = 5,
): string {
  if (!Array.isArray(properties) || properties.length === 0) return "";
  const visible = properties.slice(0, max);
  const remaining = properties.length - visible.length;
  const lines = visible
    // Some option apps ship names that already end in ":" (e.g. "Fire
    // Media Free Gift Included:") — trim it before appending our own
    // so the line doesn't render "Included::" (live-observed).
    .map(p => `<div class="qb-item-option">${escapeHtml(String(p?.name ?? "").replace(/:+$/, ""))}: ${escapeHtml(String(p?.value ?? ""))}</div>`)
    .join("");
  const more = remaining > 0
    ? `<div class="qb-item-option qb-item-option-more">+${remaining} more options</div>`
    : "";
  return lines + more;
}
